import { ChangeDetectionStrategy, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { CommonController } from '@common/classes/common.controller';
import { CustomsService } from '@common/services/customs.service';
import { FileDownloadService } from '@common/services/file-download.service';
import { ModalService } from '@common/services/modal.service';
import { marker } from '@common/translate-marker';
import { TranslateService } from '@ngx-translate/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { ExpansionPanelComponent } from '@progress/kendo-angular-layout';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { ToastService } from '@services/toast.service';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-hold-details-modal',
    templateUrl: './hold-details-modal.component.html',
    styleUrls: ['./hold-details-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HoldDetailsModalComponent extends CommonController implements OnInit {
    holdTypes: any[];
    entity: string;
    id: number;
    serviceRequestId: number;
    containerVisitId: number;
    serviceRequestVersionShipmentId: number;
    serviceRequestVersionContainerId: number;
    serviceRequestVersionShipmentDetailId: number;
    shipmentPoolId: number;
    shipmentPoolItemId: number;

    originalFormValue: any;

    @ViewChildren(ExpansionPanelComponent)
    panels: QueryList<ExpansionPanelComponent>;

    form: UntypedFormGroup;
    isBusy: boolean;
    destroyed$ = new Subject<void>();

    addedAttachments: any[] = [];
    public attachmentFileUploadRestrictions: FileRestrictions = {
        maxFileSize: 5_242_880,
        allowedExtensions: ['.pdf']
    };

    constructor(
        translateService: TranslateService,
        dialogService: ModalService,
        public activeModal: DialogRef,
        private fb: FormBuilder,
        private customsService: CustomsService,
        private toastyService: ToastService,
        public fileDownloadService: FileDownloadService
    ) {
        super(dialogService, translateService);
        this.fileDownloadService.setAttachmentFileUploadRestrictions(this.attachmentFileUploadRestrictions);
    }

    ngOnInit(): void {
        this.isBusy = true;
        this.getHoldDetails();
    }

    public onAction(index: number): void {
        this.panels.forEach((panel, idx) => {
            if (idx !== index && panel.expanded) {
                panel.toggle();
            }
        });
    }

    isFormChanged(): boolean {
        return JSON.stringify(this.originalFormValue) !== JSON.stringify(this.form.getRawValue());
    }

    saveDetails() {
        // filter out the already saved attachments
        const activeHoldDetails = this.form.value.holdTypes
            .filter((x) => x.active)
            .map((hold) => ({
                ...hold,
                attachments: hold.attachments?.filter((attachment) => attachment?.base64Content)
            }));

        this.customsService
            .saveCustomsHoldDetails({
                holdDetails: activeHoldDetails
            })
            .subscribe(() => {
                this.toastyService.success(this.translateService.instant('Hold details saved successfully'));
                this.activeModal.close();
            });
    }

    removeFile($event, holdId: number) {
        if ($event.files[0]?.attachedFile) {
            this.form.value.holdTypes
                .find((x) => x.id === holdId)
                ?.removedAttachmentIds?.push($event.files[0].attachedFile.id);
        }
    }

    fileSelected($event, holdId: number) {
        if ($event.files.length > 10) {
            this.toastyService.error(this.translateService.instant(marker('Please select maximum of 10 files')));
            $event.preventDefault();
            return;
        }

        $event.files.forEach((element) => {
            if (element.size > this.attachmentFileUploadRestrictions.maxFileSize) {
                this.toastyService.error(
                    `${this.translateService.instant(marker('Attachment size invalid'))} - ${element.name}`
                );
                return;
            }

            if (!this.attachmentFileUploadRestrictions.allowedExtensions.some((ext) => ext == element.extension)) {
                this.toastyService.error(
                    `${this.translateService.instant(marker('Invalid attachment'))} - ${element.name}`
                );
                $event.preventDefault();
                return;
            }

            const raw = element.rawFile;

            if (raw) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.documentOnLoad(e.target.result.toString(), raw.name, holdId);
                };
                reader.readAsDataURL(element.rawFile);
            }
        });
    }

    documentOnLoad(file: string, name: string, holdId: number) {
        this.form.value.holdTypes
            .find((x) => x.id === holdId)
            ?.attachments?.push({ base64Content: file, base64ContentName: name });
    }

    unholdButtonVisible() {
        if (['Service Request', 'Container Item', 'Shipment Item', 'Item'].includes(this.entity)) {
            return this.hasPermission('TosOperations.ServiceRequest.Unhold');
        } else if (this.entity === 'Container Visit') {
            return this.hasPermission('CargoOperations.ContainerVisit.Unhold');
        } else if (this.entity.startsWith('Shipment Pool')) {
            return this.hasPermission('TosOperations.ShipmentPool.Unhold');
        }
    }

    holdButtonVisible() {
        if (['Service Request', 'Container Item', 'Shipment Item', 'Item'].includes(this.entity)) {
            return this.hasPermission('TosOperations.ServiceRequest.Hold');
        } else if (this.entity === 'Container Visit') {
            return this.hasPermission('CargoOperations.ContainerVisit.Hold');
        } else if (this.entity.startsWith('Shipment Pool')) {
            return this.hasPermission('TosOperations.ShipmentPool.Hold');
        }
    }

    hasXRayRights(hold: any) {
        return this.hasPermission('Customs.CustomsHold.XRay') && hold.active && hold.cargoHoldType.id === 'HXRAY';
    }

    canEdit(active: boolean) {
        return (this.unholdButtonVisible() || this.holdButtonVisible()) && active;
    }

    private createFormGroup(): FormGroup {
        return this.fb.group({
            holdTypes: this.fb.array(this.holdTypes.map((hold) => this.createHoldFormGroup(hold)))
        });
    }

    private createHoldFormGroup(hold): FormGroup {
        return this.fb.group({
            id: new FormControl({ value: hold.id, disabled: false }),
            active: new FormControl({ value: hold.active, disabled: false }),
            cargoHoldTypeId: new FormControl({ value: hold.cargoHoldType.id, disabled: true }),
            createdDate: new FormControl({ value: hold.createdDate, disabled: true }),
            unholdedDate: new FormControl({ value: hold.unholdedDate, disabled: true }),
            remarkHold: new FormControl({ value: hold.remarkHold, disabled: !this.canEdit(hold.active) }),
            remarkUnhold: new FormControl({
                value: hold.remarkUnhold,
                disabled: true
            }),
            additionalInfo: new FormControl({
                value: hold.additionalInfo,
                disabled: !this.canEdit(hold.active) && !this.hasXRayRights(hold)
            }),
            xRay: new FormControl({
                value: hold.xRay,
                disabled: !this.canEdit(hold.active) && !this.hasXRayRights(hold)
            }),
            attachments: new FormControl({ value: hold.attachments, disabled: !this.canEdit(hold.active) }),
            removedAttachmentIds: new FormControl({ value: [], disabled: false })
        });
    }

    private getHoldDetails() {
        const filter = {
            serviceRequestId: this.serviceRequestId,
            containerVisitId: this.containerVisitId,
            serviceRequestVersionShipmentId: this.serviceRequestVersionShipmentId,
            serviceRequestVersionContainerId: this.serviceRequestVersionContainerId,
            serviceRequestVersionShipmentDetailId: this.serviceRequestVersionShipmentDetailId,
            shipmentPoolId: this.shipmentPoolId,
            shipmentPoolItemId: this.shipmentPoolItemId
        };

        this.customsService.getCustomsHoldDetails(filter).subscribe((data) => {
            this.holdTypes = data;
            this.holdTypes[0].expanded = true;
            this.form = this.createFormGroup();
            this.originalFormValue = this.form.getRawValue();
            this.isBusy = false;
        });
    }
}
